import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import {
  AccountType,
  deleteV1CompaniesCompanyIdMaterialRatesId,
  getV1CompaniesCompanyIdProjectsProjectIdMaterialRates,
  ModelError_Item,
  ModelError_Response,
  OrderUnitOfMeasure,
  patchV1CompaniesCompanyIdMaterialRatesId,
  postV1CompaniesCompanyIdMaterialRates,
} from '@treadinc/horizon-api-spec';
import { AxiosError } from 'axios';
import { t } from 'i18next';
import { isNil } from 'lodash-es';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import ConfirmationDialog from '~components/Dialog/ConfirmationDialog';
import Dialog from '~components/Dialog/Dialog';
import DialogActions from '~components/Dialog/DialogActions';
import DialogContent from '~components/Dialog/DialogContent';
import DialogHeader from '~components/Dialog/DialogHeader';
import {
  OverflowAwareText,
  SmallButton,
  SmallLoadingButton,
} from '~components/Order/ordersDispatchStyledComponents';
import ProjectPricingHistoryButton from '~components/PricingHistory/ProjectPricingHistoryButton';
import { buildQuoteLineItems } from '~components/Project/QuotePdf/buildQuoteLineItems';
import { AddOnAssignment } from '~hooks/useAddOns/models';
import { AnalyticsController } from '~hooks/useAnalytics/AnalyticsController';
import { useCompanyCurrency } from '~hooks/useCompanyCurrency/useCompanyCurrency';
import useRecordCompanySettings from '~hooks/useCompanySettings/useRecordCompanySettings';
import { MaterialRate } from '~hooks/useMaterialRates/models';
import { deparseCreateV2, deparseUpdateV2, Project } from '~hooks/useProjects/models';
import useProjectQuoteData from '~hooks/useProjects/useProjectQuoteData';
import useProjects from '~hooks/useProjects/useProjects';
import { SiteRateScheduleSaveResult } from '~hooks/useSiteRateSchedule/siteRateScheduleSaveResult';
import useSiteRateScheduleOperations from '~hooks/useSiteRateSchedule/useSiteRateScheduleOperations';
import { useSites } from '~hooks/useSites/useSites';
import { createSitesInline } from '~pages/Sales/Orders/helpers';
import { AddOnAssignmentSchema } from '~pages/Sales/Orders/NewOrderForm/components/AutomaticAddOns';
import { SiteRateScheduleOperations } from '~pages/Sales/Projects/components/SiteRatesForm';
import { ProjectFormSchemaInterface } from '~pages/Sales/Projects/projectFormSchema';
import {
  ProjectFormV2,
  type ProjectFormV2Handle,
} from '~pages/Sales/Projects/projectFormV2/ProjectFormV2';
import { ProjectFormV2Provider } from '~pages/Sales/Projects/projectFormV2/ProjectFormV2Context';
import { makeMaterialRateRequirement } from '~pages/Sales/sharedSales/rateRequirements';
import connection from '~services/connectionModule';
import { useStores } from '~store';
import { alert, AlertTypes } from '~types/AlertTypes';
import { Nullable } from '~types/Nullable';
import { fetchAllApiPages } from '~utils/fetchAllPages';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import ProjectsQueryContext from '../../ProjectsQueryContext';
import QuotePreviewDialog from './QuotePreviewDialog';
import { stripProjectIdsForCopy } from './stripProjectIdsForCopy';
import { processAddOnAssignmentsForProject } from './utils/processAddOnAssignmentsForProject';

interface ProjectDialogProps {
  creationMode?: 'project' | 'quote';
  isCopyAndEdit?: boolean;
  isOpen: boolean;
  onClose: () => void;
  project?: Project;
}

export default function ProjectDialog({
  creationMode = 'project',
  isCopyAndEdit,
  isOpen,
  onClose,
  project,
}: ProjectDialogProps) {
  const isEditing = Boolean(project) && !isCopyAndEdit;

  const projectForForm = useMemo(() => {
    if (!project) return undefined;
    if (isCopyAndEdit) {
      return stripProjectIdsForCopy(project);
    }
    return project;
  }, [project, isCopyAndEdit]);

  const projectActionsRef = useRef<ProjectFormV2Handle>(null);

  const wasDialogOpenRef = useRef(false);

  useEffect(() => {
    if (isOpen && !wasDialogOpenRef.current) {
      AnalyticsController.track('project_form_v2_opened', { is_editing: isEditing });
    }
    wasDialogOpenRef.current = isOpen;
  }, [isOpen, isEditing]);

  const { updateStoredProject } = useContext(ProjectsQueryContext);
  const { userStore, toasterStore } = useStores();

  const { createNewSite } = useSites();
  const { updateProjectV2, createProjectV2 } = useProjects();
  const { currencyFormatter } = useCompanyCurrency();
  const { fetchAddOnAssignments } = useProjectQuoteData();
  const { processSiteRateScheduleOperationsResilient } = useSiteRateScheduleOperations();

  const companyId = getEffectiveUserCompanyId(userStore);
  // The quote is a rates document: without view_rate the server nils every rate
  // value, so the preview would render blank rate columns.
  const canViewRate = Boolean(userStore.getPermissions().canViewRate);

  // Pre-fill the Company field on a new project with the user's current
  // company, mirroring the legacy form. Without this the V2 form
  // opened with an empty Company selector. setDefaultProjectValues only uses
  // this when creating (no defaultProject.company), so editing an existing
  // project still keeps its own company.
  const currentCompany = userStore.currentCompanies[0] || userStore.user.company;

  type MaterialRateFormRow = NonNullable<
    ProjectFormSchemaInterface['materialRates']
  >[number];

  const originalMaterialRatesRef = useRef<Map<string, MaterialRateFormRow>>(new Map());

  const [asyncErrors, setAsyncErrors] = useState<ModelError_Item[]>([]);
  const [hasCustomerAccount, setHasCustomerAccount] = useState(false);
  const [areRateCardsLoaded, setAreRateCardsLoaded] = useState(false);
  const [isBuildingQuotePreview, setIsBuildingQuotePreview] = useState(false);
  const [quotePreview, setQuotePreview] = useState<{
    lineItems: ReturnType<typeof buildQuoteLineItems>;
    taxRate: Nullable<number>;
  } | null>(null);
  const [loadedMaterialRates, setLoadedMaterialRates] = useState<MaterialRate[] | null>(
    null,
  );
  const [materialRatesFailedToLoad, setMaterialRatesFailedToLoad] = useState(false);

  // A required material rate is checked against every saved rate, so the form
  // needs all pages before it can validate.
  const recordCompanySettings = useRecordCompanySettings(project?.company?.id);
  const { canCreateMaterialRate, canEditMaterialRate } = userStore.getPermissions();
  const hasRecordCompanySettings = Boolean(recordCompanySettings);
  const mustLoadEveryMaterialRate = Boolean(
    makeMaterialRateRequirement(
      recordCompanySettings ?? undefined,
      canCreateMaterialRate && canEditMaterialRate,
    ),
  );

  // Fetch existing material rates on edit to populate the form
  const fetchMaterialRatesForProject = useCallback(
    async (projectId: string, loadEveryPage: boolean, signal?: AbortSignal) => {
      setLoadedMaterialRates(null);
      setMaterialRatesFailedToLoad(false);
      originalMaterialRatesRef.current = new Map();

      try {
        const rates = loadEveryPage
          ? await fetchAllApiPages(
              (query) =>
                getV1CompaniesCompanyIdProjectsProjectIdMaterialRates({
                  path: { 'company-id': companyId, 'project-id': projectId },
                  query,
                  signal,
                }),
              MaterialRate.parse,
              { signal },
            )
          : (
              await getV1CompaniesCompanyIdProjectsProjectIdMaterialRates({
                path: { 'company-id': companyId, 'project-id': projectId },
                signal,
              })
            ).data.data.map((rate) => MaterialRate.parse(rate));
        if (signal?.aborted) return;

        setLoadedMaterialRates(rates);
      } catch (error) {
        if (signal?.aborted) return;

        setMaterialRatesFailedToLoad(true);
        connection.handleRequestError(
          error,
          t('error_messages.material_rates.failed_to_fetch_material_rates'),
        );
      }
    },
    [companyId],
  );

  const shouldLoadMaterialRates = (isEditing || isCopyAndEdit) && Boolean(project?.id);

  useEffect(() => {
    if (shouldLoadMaterialRates && project?.id) {
      if (!hasRecordCompanySettings) return;

      const controller = new AbortController();
      void fetchMaterialRatesForProject(
        project.id,
        mustLoadEveryMaterialRate,
        controller.signal,
      );

      return () => controller.abort();
    }

    setLoadedMaterialRates(null);
    originalMaterialRatesRef.current = new Map();
  }, [
    shouldLoadMaterialRates,
    project?.id,
    hasRecordCompanySettings,
    mustLoadEveryMaterialRate,
    fetchMaterialRatesForProject,
  ]);

  const isAwaitingRequiredMaterialRates =
    mustLoadEveryMaterialRate && shouldLoadMaterialRates && loadedMaterialRates === null;
  const shouldOfferMaterialRatesRetry =
    isAwaitingRequiredMaterialRates && materialRatesFailedToLoad;

  // Store original material rates for diffing on submit
  useEffect(() => {
    if (loadedMaterialRates && !isCopyAndEdit) {
      const originals = new Map<string, MaterialRateFormRow>();
      loadedMaterialRates.forEach((rate) => {
        originals.set(rate.id, {
          id: rate.id,
          isDeleted: false,
          material: {
            id: rate.material?.id ?? '',
            name: rate.material?.name ?? '',
          },
          unitOfMeasure: {
            id: rate.unitOfMeasure ?? '',
            name: rate.unitOfMeasure ?? '',
          },
          materialRateSite: {
            id: rate.site?.id ?? '',
            name: rate.site?.name ?? '',
          },
          materialRateBuyPrice: rate.buyPrice ?? undefined,
          materialRateSellPrice: rate.sellPrice ?? undefined,
        });
      });
      originalMaterialRatesRef.current = originals;
    }
  }, [loadedMaterialRates, isCopyAndEdit]);
  const [showConfirmCloseDialog, setShowConfirmCloseDialog] = useState(false);

  // Locks the form during a save attempt: read-only while saving, no exit.
  // Drives the disabled state of the submit button, cancel button, and
  // underlying form fields.
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Persists the parent record we successfully POSTed in a previous
  // (partial-failure) submit attempt, even though `project` was never
  // passed in (create flow). Lets the dialog flip to update mode mid-flow
  // so the second submit re-targets the existing project rather than
  // POSTing a duplicate.
  const [createdProject, setCreatedProject] = useState<Project | null>(null);

  // Footer status messages after the most recent submit. `null` ⇒ no
  // submit attempted yet (or dialog reset). `projectStatus` covers the
  // parent record outcome; `ratesSucceededCount` / `ratesFailedCount`
  // summarize freight rate save outcomes per the mocks.
  const [lastSubmitSummary, setLastSubmitSummary] = useState<{
    addOnsFailedCount: number;
    projectStatus: 'saved' | 'failed';
    ratesAttempted: boolean;
    ratesSucceededCount: number;
    ratesFailedCount: number;
  } | null>(null);

  const hasPartialFailure = createdProject !== null;

  // Preview state and its cached assignments must not survive the dialog: a
  // reopen re-reads the form, and the assignments may have changed meanwhile.
  const resetQuotePreviewState = () => {
    setQuotePreview(null);
    setHasCustomerAccount(false);
    setAreRateCardsLoaded(false);
    quoteAddOnAssignmentsRef.current.clear();
  };

  const handleClose = async (onSubmitSuccess: boolean | null = null) => {
    // Blocks exit while a save is mid-flight ("block the user from leaving
    // the project form while changes are being saved"). `setIsSubmitting(false)`
    // runs on every submit exit-path so the user is never permanently locked in.
    if (isSubmitting) return;

    // The form shell is effect-free, so it never pushes dirty updates up to the
    // dialog. Pull the live RHF dirty flag off the handle at close time instead
    // so the "Are you sure you want to exit?" prompt still fires.
    const formIsDirty = Boolean(projectActionsRef.current?.getIsDirty());

    if (onSubmitSuccess) {
      setShowConfirmCloseDialog(false);
      onClose();
    } else if (formIsDirty || hasPartialFailure) {
      // hasPartialFailure short-circuits the dirty check: even if the user
      // hasn't edited anything since the partial failure, abandoning the
      // dialog still loses unsaved freight rate work, so we must prompt.
      setShowConfirmCloseDialog(true);
      return;
    } else {
      await projectActionsRef.current?.fileAttachmentsOnClose();
      onClose();
    }

    setAsyncErrors([]);
    setCreatedProject(null);
    setLastSubmitSummary(null);
    resetQuotePreviewState();
  };

  const confirmClose = async () => {
    await projectActionsRef.current?.fileAttachmentsOnClose();
    onClose();
    setShowConfirmCloseDialog(false);
    // Match the cleanup that handleClose runs on the normal exit path so
    // closing through the confirm dialog doesn't leak stale field-level errors
    // into the next dialog session.
    setAsyncErrors([]);
    setCreatedProject(null);
    setLastSubmitSummary(null);
    resetQuotePreviewState();
  };

  const cancelClose = () => {
    setShowConfirmCloseDialog(false);
  };

  const processProjectMaterialRates = (
    project: Project,
    projectCompanyId: string,
    materialRates: MaterialRateFormRow[],
    projectMaterialTotals?: NonNullable<
      ProjectFormSchemaInterface['projectMaterialTotals']
    >,
  ) => {
    const originals = originalMaterialRatesRef.current;

    const toCreate: Promise<unknown>[] = [];
    const toUpdate: Promise<unknown>[] = [];
    const toDelete: Promise<unknown>[] = [];

    for (const rate of materialRates) {
      // Rate UoM is the source of truth. Parent material total UoM is
      // only used as a fallback for brand-new rows that have no unit yet.
      const parentTotal = (projectMaterialTotals ?? []).find(
        (_mt, idx) => idx === rate.parentIndex,
      );
      // The parent total owns the material: the rate row's copy is stamped when
      // the row is added and never re-pointed, so it goes stale the moment the
      // total's material changes.
      const materialId = parentTotal?.material?.id;
      const rateUomId = (rate.unitOfMeasure as { id: string })?.id;
      const parentUomId = (parentTotal?.unitOfMeasure as { id: string })?.id;
      const uomId = rateUomId || parentUomId;
      const siteId = rate.materialRateSite?.id || undefined;
      const buyPrice = !isNil(rate.materialRateBuyPrice)
        ? Number(rate.materialRateBuyPrice)
        : null;
      const sellPrice = !isNil(rate.materialRateSellPrice)
        ? Number(rate.materialRateSellPrice)
        : null;

      const hasRateData = siteId || buyPrice !== null || sellPrice !== null;

      if (rate.isDeleted && rate.id) {
        toDelete.push(
          deleteV1CompaniesCompanyIdMaterialRatesId({
            path: { 'company-id': projectCompanyId, id: String(rate.id) },
          }),
        );
      } else if (!rate.id && materialId && hasRateData) {
        toCreate.push(
          postV1CompaniesCompanyIdMaterialRates({
            path: { 'company-id': projectCompanyId },
            body: {
              material_id: String(materialId),
              project_id: project.id,
              site_id: siteId,
              customer_account_id: String(project.account?.id),
              buy_price: buyPrice,
              sell_price: sellPrice,
              unit_of_measure: (uomId || '') as OrderUnitOfMeasure,
            },
          }),
        );
      } else if (rate.id && materialId && originals.has(rate.id)) {
        const original = originals.get(rate.id);
        const changed =
          materialId !== original?.material?.id ||
          uomId !== (original?.unitOfMeasure as { id: string })?.id ||
          siteId !== (original?.materialRateSite?.id || undefined) ||
          buyPrice !==
            (!isNil(original?.materialRateBuyPrice)
              ? Number(original.materialRateBuyPrice)
              : null) ||
          sellPrice !==
            (!isNil(original?.materialRateSellPrice)
              ? Number(original.materialRateSellPrice)
              : null);

        if (changed) {
          toUpdate.push(
            patchV1CompaniesCompanyIdMaterialRatesId({
              path: { 'company-id': projectCompanyId, id: String(rate.id) },
              body: {
                material_id: String(materialId),
                site_id: siteId || null,
                buy_price: buyPrice,
                sell_price: sellPrice,
                unit_of_measure: (uomId || '') as OrderUnitOfMeasure,
              },
            }),
          );
        }
      } else if (!materialId && (rate.id || hasRateData)) {
        console.warn(
          'Skipped a project material rate: its parent material total resolved no material',
          { rateId: rate.id ?? null, parentIndex: rate.parentIndex },
        );
      }
    }

    return Promise.all([...toCreate, ...toUpdate, ...toDelete]).catch((error) => {
      connection.handleRequestError(
        error,
        t('error_messages.projects.failed_to_process_material_rates_for_project'),
      );
    });
  };

  const onNewProjectSuccess = async (project?: Project) => {
    if (project) {
      await onSuccess(project);
    }
  };

  const onSuccess = async (project: Project) => {
    await projectActionsRef.current?.fileAttachmentsOnSubmit(project.id);
    await handleClose(true);
    updateStoredProject(project);

    toasterStore.push(
      alert(
        isEditing
          ? t('account.account_updated', { name: project.name })
          : t('account.account_created', { name: project.name }),
        AlertTypes.success,
      ),
    );
  };

  const onSubmitProjectFormCallBack = async (
    data: ProjectFormSchemaInterface,
    customerRateAddOnAssignments?: AddOnAssignmentSchema[],
    vendorRateAddOnAssignments?: AddOnAssignmentSchema[],
    driverRateAddOnAssignments?: AddOnAssignmentSchema[],
    siteRateScheduleOperations?: SiteRateScheduleOperations,
    projectMaterialTotalsForRates?: NonNullable<
      ProjectFormSchemaInterface['projectMaterialTotals']
    >,
  ) => {
    const materialTotalsForRates =
      projectMaterialTotalsForRates ?? data.projectMaterialTotals ?? [];

    setIsSubmitting(true);
    setAsyncErrors([]);

    // When a previous submit produced a partial failure we already have a
    // server-side project. Treat this submit as an update even though the
    // dialog originally opened in create mode, so we don't POST a
    // duplicate parent.
    const treatAsUpdate = isEditing || createdProject !== null;

    AnalyticsController.track('project_form_v2_submit_started', {
      is_editing: isEditing,
    });

    let savedProject: Project | null = null;
    try {
      // `createSitesInline` lives inside the try so a throw here doesn't
      // skip the catch that resets `isSubmitting`. Every
      // `await` must be inside the try block; previously a network failure
      // during inline site creation could leave the dialog locked with a
      // spinning submit button and a disabled cancel.
      const newData = await createSitesInline(
        data,
        createNewSite,
        userStore.userCompany?.siteRadius,
      );

      if ('error' in newData) {
        const serverMessage =
          'message' in newData && typeof newData.message === 'string'
            ? newData.message
            : null;
        const siteCreationErrorMessage =
          serverMessage ??
          (newData.field === 'pickUpWayPoint'
            ? t('error_messages.orders.failed_to_create_pickup_site')
            : t('error_messages.orders.failed_to_create_dropoff_site'));

        toasterStore.push(alert(siteCreationErrorMessage, AlertTypes.error));
        setLastSubmitSummary({
          projectStatus: 'failed',
          ratesAttempted: false,
          ratesSucceededCount: 0,
          ratesFailedCount: 0,
          addOnsFailedCount: 0,
        });
        setIsSubmitting(false);
        return;
      }

      if (treatAsUpdate) {
        const dataForUpdate = createdProject
          ? ({ ...newData, id: createdProject.id } as ProjectFormSchemaInterface)
          : (newData as ProjectFormSchemaInterface);

        // Sparse PATCH: deparseUpdateV2 emits only the user-touched fields,
        // gated on the form's dirtyFields snapshot (captured via the handle's
        // getDirtyFields()). When the result is `{}` the user clicked submit
        // without any parent-level change — skip the PATCH entirely and reuse
        // the existing project so downstream operations (material rates,
        // add-ons, site rates) can still process their own dirty state.
        const dirtyFields = projectActionsRef.current?.getDirtyFields?.() ?? {};
        const updateBody = deparseUpdateV2(dataForUpdate, dirtyFields);
        if (Object.keys(updateBody).length === 0) {
          // treatAsUpdate=true requires `isEditing || createdProject !== null`,
          // so one of `project` / `createdProject` is always defined. The
          // fallback throw keeps savedProject narrowed for the downstream code
          // without an `!` non-null assertion.
          const existing = createdProject ?? project ?? null;
          if (!existing) {
            throw new Error('Empty-PATCH skip: no project to reuse');
          }
          savedProject = existing;
        } else {
          savedProject = await updateProjectV2({
            id: dataForUpdate.id ?? '',
            body: updateBody,
          });
        }
      } else {
        const createBody = deparseCreateV2(newData as ProjectFormSchemaInterface);
        savedProject = await createProjectV2({
          initialState: creationMode === 'quote' ? 'draft' : 'active',
          body: createBody,
        });
        setCreatedProject(savedProject);
      }
    } catch (error) {
      const modelError = error as AxiosError<ModelError_Response>;
      // Optional-chain the full path: a non-422 failure (network error, 500
      // with a non-JSON body, etc.) has no `data.error.errors`, and reading
      // `.error.errors` off an undefined `error` threw a TypeError here —
      // skipping the `setIsSubmitting(false)` below and leaving both the
      // Cancel and Create buttons permanently stuck.
      const errorItems = modelError.response?.data?.error?.errors || [];
      setAsyncErrors(errorItems);
      AnalyticsController.track('project_form_v2_submit_failed', {
        is_editing: isEditing,
        error_status: modelError.response?.status,
        error_count: errorItems.length,
      });
      setLastSubmitSummary({
        projectStatus: 'failed',
        ratesAttempted: false,
        ratesSucceededCount: 0,
        ratesFailedCount: 0,
        addOnsFailedCount: 0,
      });
      setIsSubmitting(false);
      // The parent POST/PATCH failed: surface field-level errors,
      // attempt no downstream calls, leave the dialog open as today.
      return;
    }

    // Material rates / parent-level add-ons remain out of scope for this
    // resilience phase: they keep their existing best-effort behavior of
    // surfacing a single toast on failure. Only freight rates participate
    // in the per-op recovery flow.
    const projectCompanyId = savedProject.company?.id || companyId;
    const materialRatesOperations = processProjectMaterialRates(
      savedProject,
      projectCompanyId,
      data.materialRates ?? [],
      materialTotalsForRates,
    );

    const customerAddOnAssignmentsOperations = customerRateAddOnAssignments
      ? processAddOnAssignmentsForProject({
          companyId: projectCompanyId,
          project: savedProject,
          addOnAssignments: customerRateAddOnAssignments,
          accountType: AccountType.CUSTOMER,
        })
      : Promise.resolve();

    const vendorAddOnAssignmentsOperations = vendorRateAddOnAssignments
      ? processAddOnAssignmentsForProject({
          companyId: projectCompanyId,
          project: savedProject,
          addOnAssignments: vendorRateAddOnAssignments,
          accountType: AccountType.VENDOR,
        })
      : Promise.resolve();

    const driverAddOnAssignmentsOperations = driverRateAddOnAssignments
      ? processAddOnAssignmentsForProject({
          companyId: projectCompanyId,
          project: savedProject,
          addOnAssignments: driverRateAddOnAssignments,
        })
      : Promise.resolve();

    const siteRateScheduleResultPromise: Promise<SiteRateScheduleSaveResult | null> =
      siteRateScheduleOperations
        ? processSiteRateScheduleOperationsResilient(
            {
              type: 'project',
              companyId: projectCompanyId,
              projectId: savedProject.id,
            },
            siteRateScheduleOperations,
            'project',
          )
        : Promise.resolve(null);

    // Material rates and parent-level add-on helpers both delegate to
    // `connection.handleRequestError`, which re-throws after toasting. Use
    // `Promise.allSettled` so a single rejection doesn't short-circuit the
    // other branches, and count attempted add-ons per failed batch so the
    // footer can show exactly how many didn't land instead of relying on
    // an opaque toast. Material-rate failures keep their existing
    // `handleRequestError` toast (no need to also count them here).
    const customerAddOnCount = customerRateAddOnAssignments?.length ?? 0;
    const vendorAddOnCount = vendorRateAddOnAssignments?.length ?? 0;
    const driverAddOnCount = driverRateAddOnAssignments?.length ?? 0;

    const settled = await Promise.allSettled([
      materialRatesOperations,
      customerAddOnAssignmentsOperations,
      vendorAddOnAssignmentsOperations,
      driverAddOnAssignmentsOperations,
      siteRateScheduleResultPromise,
    ]);

    // The save may have changed the assignments a later preview would read,
    // so the cache from before it is stale even when the dialog stays open.
    quoteAddOnAssignmentsRef.current.clear();

    const addOnsFailedCount =
      (settled[1].status === 'rejected' ? customerAddOnCount : 0) +
      (settled[2].status === 'rejected' ? vendorAddOnCount : 0) +
      (settled[3].status === 'rejected' ? driverAddOnCount : 0);

    const materialRatesFailed = settled[0].status === 'rejected';
    const siteRateResult: SiteRateScheduleSaveResult | null =
      settled[4].status === 'fulfilled' ? settled[4].value : null;

    if (addOnsFailedCount > 0 || materialRatesFailed) {
      // Project landed (savedProject is non-null); some downstream items
      // didn't. Surface the add-on count inline in the footer so the user
      // sees exactly what needs another submit. Material-rate failures
      // already toast via `handleRequestError` inside
      // `processProjectMaterialRates`.
      if (settled[1].status === 'rejected') {
        const r = settled[1].reason as AxiosError<ModelError_Response>;
        setAsyncErrors(r.response?.data?.error?.errors || []);
      }
      AnalyticsController.track('project_form_v2_submit_partial', {
        is_editing: isEditing,
        add_ons_failed_count: addOnsFailedCount,
        material_rates_failed: materialRatesFailed,
      });
      setLastSubmitSummary({
        projectStatus: 'saved',
        ratesAttempted: false,
        ratesSucceededCount: 0,
        ratesFailedCount: 0,
        addOnsFailedCount,
      });
      setIsSubmitting(false);
      return;
    }

    let ratesSummary:
      | { hasFailures: boolean; succeededCount: number; failedCount: number }
      | undefined;
    if (siteRateResult) {
      ratesSummary = projectActionsRef.current?.applySiteRateSaveResult(siteRateResult);
    }

    const ratesAttempted = Boolean(
      siteRateScheduleOperations &&
      siteRateScheduleOperations.toCreate.length +
        siteRateScheduleOperations.toUpdate.length +
        siteRateScheduleOperations.toDelete.length >
        0,
    );
    const ratesFailed = ratesSummary?.failedCount ?? 0;
    const ratesSucceeded = ratesSummary?.succeededCount ?? 0;

    setLastSubmitSummary({
      projectStatus: 'saved',
      ratesAttempted,
      ratesSucceededCount: ratesSucceeded,
      ratesFailedCount: ratesFailed,
      addOnsFailedCount: 0,
    });

    if (ratesFailed > 0) {
      // Parent saved, but at least one rate failed — keep the dialog
      // open in update mode so the user can fix the offending rows and
      // resubmit. The freight-rates form already has the per-card error
      // markers and updated server IDs from `applySiteRateSaveResult`.
      AnalyticsController.track('project_form_v2_submit_partial', {
        is_editing: isEditing,
        rates_failed_count: ratesFailed,
        rates_succeeded_count: ratesSucceeded,
      });
      // TRE-16170: a durable dereference (the `rate_reference` call) ran and SUCCEEDED before the card
      // writes, but a card write then failed — so the deselected orders now keep their own rates while
      // the user's rate edit did NOT land. Say which half succeeded so the badge ("keeps its own rates")
      // isn't confusing next to an unsaved edit. The endpoint is idempotent, so a full retry is safe.
      if (siteRateScheduleOperations?.rateReferenceApplied) {
        toasterStore.push(
          alert(t('project.form.rate_reference_partial_failure'), AlertTypes.warn),
        );
      }
      setIsSubmitting(false);
      return;
    }

    AnalyticsController.track('project_form_v2_submit_succeeded', {
      is_editing: isEditing,
      section_ms: projectActionsRef.current?.getSectionMs(),
    });

    // All-good path: behavior unchanged from today (close dialog, refresh
    // list, success toast).
    if (isEditing) {
      await onSuccess(savedProject);
    } else {
      await onNewProjectSuccess(savedProject);
    }
    setIsSubmitting(false);
    setCreatedProject(null);
    setLastSubmitSummary(null);
  };

  const onSubmitProjectForm = () => {
    projectActionsRef.current?.submit(onSubmitProjectFormCallBack);
  };

  // Cached per preview click so repeat clicks do not re-walk the assignments
  // endpoint; invalidated on close and after a save, either of which can change
  // what the server holds.
  const quoteAddOnAssignmentsRef = useRef<Map<string, AddOnAssignment[]>>(new Map());
  const resolveQuoteAddOnAssignments = useCallback(
    async (savedProjectId: string): Promise<AddOnAssignment[]> => {
      const cached = quoteAddOnAssignmentsRef.current.get(savedProjectId);
      if (cached) return cached;

      const assignments = await fetchAddOnAssignments(
        (createdProject ?? project)?.company?.id || companyId,
        savedProjectId,
      );
      quoteAddOnAssignmentsRef.current.set(savedProjectId, assignments);
      return assignments;
    },
    [companyId, createdProject, project, fetchAddOnAssignments],
  );

  const onPreviewQuote = async () => {
    setIsBuildingQuotePreview(true);

    try {
      const previewInput = projectActionsRef.current?.getQuotePreviewInput();
      if (!previewInput) return;

      // Add-on assignments are the only part of the quote that still lives
      // solely on the server; an unsaved project has none yet.
      const savedProjectId = createdProject?.id ?? project?.id;
      const addOnAssignments = savedProjectId
        ? await resolveQuoteAddOnAssignments(savedProjectId)
        : [];

      setQuotePreview({
        lineItems: buildQuoteLineItems({
          addOnAssignments,
          currencyFormatter,
          materialRates: previewInput.materialRates,
          project: previewInput.project,
          siteRateSchedules: previewInput.siteRateSchedules,
        }),
        taxRate: previewInput.project.taxRate,
      });
    } catch (error) {
      try {
        connection.handleRequestError(
          error,
          t('error_messages.projects.failed_to_build_quote_preview'),
        );
      } catch {
        // Already surfaced as a toast; keep the dialog usable.
      }
    } finally {
      setIsBuildingQuotePreview(false);
    }
  };

  return (
    <>
      <Dialog
        isOpen={isOpen}
        maxWidth="lg"
        onClose={handleClose}
        preventCloseOnBackdropClick
      >
        <DialogHeader onClose={handleClose}>
          <OverflowAwareText sx={{ flex: 1, fontSize: '16px', fontWeight: 600 }}>
            {/*
              Title flips Create→Update once the parent record has been
              created in a previous (partial-failure) submit, so the user
              understands the second submit targets the existing project
              rather than creating another one.
            */}
            {isCopyAndEdit
              ? t('project.copy_and_edit')
              : project || createdProject
                ? t('project.update_project')
                : t('project.create_project')}

            {project && !isCopyAndEdit && (
              <Typography
                component="span"
                sx={(theme) => ({
                  color: theme.brandV2.colors.treadGray3,
                  fontSize: 'inherit',
                  fontWeight: 400,
                  ml: 1,
                })}
              >
                {project.projectId}
              </Typography>
            )}
          </OverflowAwareText>

          {project && !isCopyAndEdit && (
            <ProjectPricingHistoryButton
              analyticsSurface="project_form"
              label={t('project.timeline.title')}
              project={project}
            />
          )}
        </DialogHeader>

        <DialogContent
          sx={(theme) => ({
            bgcolor: theme.semantic.surfaceSunken,
            '& > div': { py: 0 },
          })}
        >
          {/*
            The Provider OWNS useForm and renders <FormProvider> internally;
            the shell consumes via useFormContext and exposes the handle the
            dialog wires against. Async errors flow into the Provider so the
            inline-error mapping effect can match BE field names to schema
            keys; the shell stays effect-free per the panel-grep gate.
            The `project-form` data-test-id is the stable e2e contract the
            shared project page objects (ProjectFormDialog,
            FreightRatesManagerSection) and flows scope every selector to. The
            shell keeps its own `project-form-v2` root (pinned by the unit
            tests); this wrapper re-exposes the hook the e2e suite drives.
          */}
          <Box data-test-id="project-form">
            <ProjectFormV2Provider
              defaultProject={projectForForm as Project}
              defaultCompany={currentCompany}
              errors={asyncErrors}
              initialMaterialRates={loadedMaterialRates}
              isCopyAndEdit={isCopyAndEdit}
              onCustomerAccountChange={setHasCustomerAccount}
              onRateCardsLoadedChange={setAreRateCardsLoaded}
              sourceProjectId={isCopyAndEdit ? project?.id : undefined}
            >
              <ProjectFormV2 ref={projectActionsRef} />
            </ProjectFormV2Provider>
          </Box>
        </DialogContent>

        <DialogActions>
          {canViewRate && (
            <SmallLoadingButton
              color="secondary"
              disabled={!hasCustomerAccount || !areRateCardsLoaded}
              loading={isBuildingQuotePreview}
              onClick={onPreviewQuote}
              sx={{ mr: 'auto' }}
              variant="outlined"
            >
              {t('project.quote_preview.action')}
            </SmallLoadingButton>
          )}
          {/*
            Footer status messages (mocks). After a submit attempt we
            render a small message row to the left of the CTAs summarizing
            project + freight-rate save outcomes.
          */}
          {lastSubmitSummary && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                pr: 1,
              }}
            >
              <Typography
                variant="body2"
                sx={(th) => ({
                  fontWeight: 600,
                  color:
                    lastSubmitSummary.projectStatus === 'saved'
                      ? th.palette.success.main
                      : th.palette.brandV2Red.main,
                })}
              >
                {lastSubmitSummary.projectStatus === 'saved'
                  ? t('project.form.project_saved')
                  : t('project.form.project_failed_to_save')}
              </Typography>
              {lastSubmitSummary.ratesAttempted && (
                <Typography
                  variant="body2"
                  sx={(th) => ({
                    fontWeight: 600,
                    color:
                      lastSubmitSummary.ratesFailedCount > 0
                        ? th.palette.brandV2Red.main
                        : th.palette.success.main,
                  })}
                >
                  {lastSubmitSummary.ratesFailedCount > 0
                    ? t('project.form.rates_failed_to_save', {
                        count: lastSubmitSummary.ratesFailedCount,
                      })
                    : t('project.form.rates_saved', {
                        count: lastSubmitSummary.ratesSucceededCount,
                      })}
                </Typography>
              )}
              {lastSubmitSummary.addOnsFailedCount > 0 && (
                <Typography
                  variant="body2"
                  sx={(th) => ({
                    fontWeight: 600,
                    color: th.palette.brandV2Red.main,
                  })}
                >
                  {t('project.form.rate_addon_save_failed', {
                    count: lastSubmitSummary.addOnsFailedCount,
                  })}
                </Typography>
              )}
            </Box>
          )}
          {shouldOfferMaterialRatesRetry && project?.id && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 1 }}>
              <Typography
                variant="body2"
                sx={(th) => ({ fontWeight: 600, color: th.palette.brandV2Red.main })}
              >
                {t('project.form.material_rates_failed_to_load')}
              </Typography>
              <SmallButton
                color="secondary"
                onClick={() => void fetchMaterialRatesForProject(project.id, true)}
                variant="outlined"
              >
                {t('actions.retry')}
              </SmallButton>
            </Box>
          )}
          <SmallButton
            color="secondary"
            disabled={isSubmitting}
            onClick={() => handleClose(null)}
            variant="outlined"
          >
            {t('actions.cancel')}
          </SmallButton>

          <SmallLoadingButton
            data-ui="page-sales__projects-v2__project-form-dialog__submit-button"
            disabled={shouldOfferMaterialRatesRetry}
            loading={
              isSubmitting ||
              (isAwaitingRequiredMaterialRates && !materialRatesFailedToLoad)
            }
            onClick={onSubmitProjectForm}
          >
            {/*
              Once the parent record exists the primary CTA shows
              "Submit" — even if the dialog opened in create mode.
            */}
            {isEditing || createdProject ? t('actions.submit') : t('actions.create')}
          </SmallLoadingButton>
        </DialogActions>
      </Dialog>

      {quotePreview && (
        <QuotePreviewDialog
          currencyFormatter={currencyFormatter}
          isOpen
          lineItems={quotePreview.lineItems}
          onClose={() => setQuotePreview(null)}
          taxRate={quotePreview.taxRate}
        />
      )}

      <ConfirmationDialog
        cancelButtonProps={{ children: t('actions.no') }}
        confirmButtonProps={{ color: 'primary', children: t('actions.exit') }}
        isOpen={showConfirmCloseDialog}
        onClose={cancelClose}
        onConfirm={confirmClose}
        title={
          hasPartialFailure
            ? t('project.confirm_exit_partial_failure_title')
            : t('project.confirm_exit_title')
        }
      >
        <Typography>
          {hasPartialFailure
            ? t('project.confirm_exit_partial_failure_text', {
                count: lastSubmitSummary?.ratesFailedCount ?? 0,
              })
            : t('project.confirm_exit_text')}
        </Typography>
      </ConfirmationDialog>
    </>
  );
}
